import React, { FC, useCallback, useState, useEffect } from "react";
import { useTranslation } from "@familyzone/component-library";
import { BasePill } from "./BasePill";
import { DeviceOptions } from "../../../types/Device";
import moment from "moment/moment";
import {
  CredentialState,
  CredentialStatus as SyncCredentialStatus,
  getLdapRun,
  LegacyLdapProviderConfig,
  listSyncExecutions,
  mapExecutions,
  mapStatusNode,
  SyncConfigurationNode,
  SyncType,
} from "../../../utils/api/SyncApi";
import { SyncStatus as SyncExecutionStatus } from "../interfaces/GetSyncExecutions";
import { formatString } from "../SyncConfig/SyncStatus";

// Enums for better type safety
enum HealthStatus {
  HEALTHY = "healthy",
  WARNING = "warning",
  CRITICAL = "critical",
  UNKNOWN = "unknown",
}

enum Provider {
  GOOGLE = "Google",
  AZURE = "Azure",
  LDAP = "LDAP",
}

export type SyncPillType = "GOOGLE" | "LDAPS" | "AZURE";

type SyncPillProps = {
  syncType: SyncPillType; // we don't allow wonde or oneroster because they can have many syncs
  deviceInfo: DeviceOptions | null;
  redirect: () => void;
  /**
   * The SM-API `/api/syncs` nodes behind this pill: the Google or Azure singleton,
   * or for LDAP the legacy configuration plus every LDAP server.
   */
  syncs: SyncConfigurationNode[];
};

type CredentialStatus = {
  provider: Provider;
  status: HealthStatus;
  message: string;
};

// Constants
const STATUS_CONFIG = {
  [HealthStatus.HEALTHY]: { color: "#28a745", icon: "fa-check-circle" },
  [HealthStatus.WARNING]: { color: "#ffc107", icon: "fa-exclamation-triangle" },
  [HealthStatus.CRITICAL]: { color: "#dc3545", icon: "fa-times-circle" },
  [HealthStatus.UNKNOWN]: { color: "#6c757d", icon: "fa-question-circle" },
} as const;

// Utility functions
const getStatusConfig = (status: HealthStatus) => STATUS_CONFIG[status];

const createCredentialStatus = (provider: Provider, status: HealthStatus, message: string): CredentialStatus => ({
  provider,
  status,
  message,
});

// We can't just update the casing because LDAP is all upper case
export const syncTypeToFriendly = (syncType: SyncPillType | "CLEVER"): string => {
  switch (syncType) {
    case "GOOGLE":
      return "Google";
    case "LDAPS":
      return "LDAP";
    case "CLEVER":
      return "Clever";
    case "AZURE":
      return "Microsoft Entra ID";
  }
};

const credentialOf = (node?: SyncConfigurationNode): SyncCredentialStatus | undefined =>
  (node?.config as { credential?: SyncCredentialStatus } | null | undefined)?.credential;

/**
 * True for the legacy LDAP configuration when it is enabled and its user sync is on.
 * That is the one case where it, rather than the LDAP servers, drives the LDAP pill:
 * its status, its credential message and where clicking it goes.
 */
export const isLegacyLdapActive = (node: SyncConfigurationNode): boolean =>
  node.syncType === SyncType.Ldap && node.enabled && ((node.config as LegacyLdapProviderConfig | null | undefined)?.syncEnabled ?? false);

/**
 * The node the pill represents. For LDAP the active legacy configuration wins,
 * otherwise any enabled LDAP server stands in for the group.
 */
export const pickActiveSync = (syncType: SyncPillType, syncs: SyncConfigurationNode[]): SyncConfigurationNode | undefined => {
  if (syncType !== "LDAPS") return syncs.find((s) => s.enabled) ?? syncs[0];
  const legacy = syncs.find((s) => s.syncType === SyncType.Ldap);
  const servers = syncs.filter((s) => s.syncType === SyncType.Ldaps);
  if (legacy && isLegacyLdapActive(legacy)) return legacy;
  return servers.find((s) => s.enabled) ?? legacy ?? servers[0];
};

// Credential analysis functions
const analyzeCredentialState = (provider: Provider, node?: SyncConfigurationNode): CredentialStatus => {
  switch (credentialOf(node)?.state) {
    case CredentialState.Set:
      return createCredentialStatus(provider, HealthStatus.HEALTHY, "Config. Status Ok");
    case CredentialState.Expired:
      return createCredentialStatus(provider, HealthStatus.CRITICAL, "Credentials expired");
    case CredentialState.Revoked:
      return createCredentialStatus(provider, HealthStatus.CRITICAL, "Credentials revoked");
    default:
      return createCredentialStatus(provider, HealthStatus.UNKNOWN, "No credentials configured");
  }
};

// Same precedence as pickActiveSync: active legacy, then any enabled server, and only
// then is a legacy configuration with its user sync off worth warning about. When one
// is active, its bind credential is checked the same way as Google's and Entra's.
const analyzeLdapCredentials = (syncs: SyncConfigurationNode[], active?: SyncConfigurationNode): CredentialStatus => {
  const legacy = syncs.find((s) => s.syncType === SyncType.Ldap);
  const hasEnabledServer = syncs.some((s) => s.syncType === SyncType.Ldaps && s.enabled);

  if ((legacy && isLegacyLdapActive(legacy)) || hasEnabledServer) {
    return analyzeCredentialState(Provider.LDAP, active);
  }
  if (legacy?.enabled) {
    return createCredentialStatus(Provider.LDAP, HealthStatus.WARNING, "LDAP enabled, user sync disabled");
  }
  return createCredentialStatus(Provider.LDAP, HealthStatus.UNKNOWN, "LDAP service disabled");
};

const analyzeCredentials = (syncType: SyncPillType, syncs: SyncConfigurationNode[], active?: SyncConfigurationNode): CredentialStatus => {
  switch (syncType) {
    case "GOOGLE":
      return analyzeCredentialState(Provider.GOOGLE, active);
    case "AZURE":
      return analyzeCredentialState(Provider.AZURE, active);
    case "LDAPS":
      return analyzeLdapCredentials(syncs, active);
  }
};

// Status timestamps arrive in the legacy format mapExecutions / mapStatusNode emit.
const statusTime = (createdAt: string): moment.Moment => moment.utc(createdAt, formatString).local();

/**
 * The statuses (newest first) of the latest run behind the pill. Google, Entra and the
 * active legacy LDAP configuration have their own executions; LDAP servers share one
 * appliance-wide run, so their status comes from that run instead.
 */
const fetchLatestStatuses = async (active: SyncConfigurationNode): Promise<SyncExecutionStatus[]> => {
  if (active.syncType === SyncType.Ldaps || (active.syncType === SyncType.Ldap && !isLegacyLdapActive(active))) {
    const run = await getLdapRun();
    return run ? (run.statuses ?? []).map((s) => mapStatusNode(s, run.id)) : [];
  }
  const executions = mapExecutions(await listSyncExecutions(active.id));
  return executions[0]?.syncStatuses ?? [];
};

export const SyncPill: FC<SyncPillProps> = ({ syncType, deviceInfo, redirect, syncs }) => {
  const { t } = useTranslation();

  const [loading, setLoading] = useState<boolean>(true);
  const [syncErrors, setSyncErrors] = useState("");
  const [syncStartTime, setSyncStartTime] = useState<moment.Moment | null>(null);
  const [credentialStatus, setCredentialStatus] = useState<CredentialStatus | null>(null);

  // Check if this is a child device (has parent_id and is not a parent)
  const isChildDevice = deviceInfo && deviceInfo.parent_id !== null && deviceInfo.parent_id !== "" && !deviceInfo.is_parent;

  const activeSync = pickActiveSync(syncType, syncs);

  const fetchStatuses = useCallback(() => {
    // Skip API call for child devices to improve performance
    if (isChildDevice) {
      setLoading(false);
      return;
    }

    setCredentialStatus(analyzeCredentials(syncType, syncs, activeSync));
    setSyncErrors("");
    setSyncStartTime(null);

    // Without a sync configuration there is no run to show.
    if (!activeSync) {
      setLoading(false);
      return;
    }

    setLoading(true);

    fetchLatestStatuses(activeSync)
      .then((statuses) => {
        const latest = statuses[0];
        const oldest = statuses[statuses.length - 1];

        // latest sync status
        if (latest?.status === "Failed") {
          setSyncErrors(latest.message);
        }
        // the oldest status of the latest run is 'started' or 'pending'
        if (oldest?.status === "Started") {
          setSyncStartTime(statusTime(oldest.createdAt));
        } else if (oldest?.status === "Pending" && statuses.length > 1) {
          // set time to the actual starting time
          setSyncStartTime(statusTime(statuses[statuses.length - 2].createdAt));
        }
      })
      .catch(() => {
        // Leave the pill in its "unable to find most recent status" state.
      })
      .finally(() => setLoading(false));
  }, [syncType, isChildDevice, syncs, activeSync]);

  useEffect(() => {
    fetchStatuses();
  }, [fetchStatuses]);

  // Determine overall status based on both sync and credential status
  const getOverallStatus = () => {
    // Priority: credential issues > sync errors > sync status
    if (credentialStatus?.status === HealthStatus.CRITICAL) {
      return {
        title: t("Critical Issue"),
        subtitle: credentialStatus.message,
        iconColor: getStatusConfig(HealthStatus.CRITICAL).color,
        iconName: getStatusConfig(HealthStatus.CRITICAL).icon,
      };
    }

    if (syncErrors !== "") {
      return {
        title: t("Failed to sync"),
        subtitle: t("Sync error occurred"),
        iconColor: getStatusConfig(HealthStatus.CRITICAL).color,
        iconName: getStatusConfig(HealthStatus.CRITICAL).icon,
      };
    }

    if (credentialStatus?.status === HealthStatus.WARNING) {
      return {
        title: t("Needs Attention"),
        subtitle: credentialStatus.message,
        iconColor: getStatusConfig(HealthStatus.WARNING).color,
        iconName: getStatusConfig(HealthStatus.WARNING).icon,
      };
    }

    if (syncStartTime) {
      return {
        title: `${t("Synced")} ${syncStartTime.from(moment())}`,
        subtitle: credentialStatus?.message || t("All systems operational"),
        iconColor: getStatusConfig(HealthStatus.HEALTHY).color,
        iconName: getStatusConfig(HealthStatus.HEALTHY).icon,
      };
    }

    return {
      title: t("Unable to find most recent status"),
      subtitle: credentialStatus?.message || t("Status unknown"),
      iconColor: getStatusConfig(HealthStatus.UNKNOWN).color,
      iconName: getStatusConfig(HealthStatus.UNKNOWN).icon,
    };
  };

  // For child devices, show a message indicating sync is managed by parent
  if (isChildDevice) {
    return (
      <BasePill
        isLoading={loading}
        title={`${t("Unable to find most recent status")}`}
        body={t(`${syncTypeToFriendly(syncType)} Sync`)}
        onClick={redirect}
      />
    );
  }

  const status = getOverallStatus();

  return (
    <BasePill
      isLoading={loading}
      title={status.title}
      subtitle={status.subtitle}
      body={t(`${syncTypeToFriendly(syncType)} Sync`)}
      onClick={redirect}
      iconColor={status.iconColor}
      iconName={status.iconName}
    />
  );
};
